import React from 'react'
import { Seg } from '@/components/sign/templates/parts'
import { Building2, Globe, Briefcase, MapPin, TrendingUp, Sparkles, Calendar, Hash } from 'lucide-react'
import { OpeningsEditor } from '@/components/jobs/openings/OpeningsEditor'
import { useJobOpenings } from '@/hooks/useJobOpenings'
import { OpeningRow, setupSummaryText } from '@/lib/jobOpenings'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { CurrencySelect } from '@/components/ui/currency-select'
import { Button } from '@/components/ui/button'
import {
  SectionCard,
  FieldLabel,
  FieldHint,
  Segmented,
  ToggleRow,
  ChipInput,
  SalaryInput,
  AiAssistedBadge,
} from './_parts'
import { CreateJobData, JobWorkMode, JobEmploymentType } from '@/hooks/useJobs'
import { useChildOrganizationsForJobCreation } from '@/hooks/useChildOrganizationsForJobCreation'
import { useAuth } from '@/contexts/AuthContext'
import { usePermissions } from '@/hooks/usePermissions'
import { ClientsManagedNote } from '@/components/organizations/ClientsManagedNote'
import { supabase } from '@/integrations/supabase/client'
import { toast } from 'sonner'
import { useDepartments } from '@/hooks/useDepartments'
import { DepartmentFormDialog } from '@/components/settings/DepartmentFormDialog'
import { JobPriorityPicker } from '@/components/jobs/JobPriorityPicker'
import { SkillPriorityLegend } from '@/components/jobs/SkillPill'
import { DEFAULT_JOB_PRIORITY } from '@/lib/job-priority'
import { confirmDialog } from '@/components/ui/confirm-dialog'
import { JobFilesField } from './JobFilesField'
import { SkillSuggestions } from './SkillSuggestions'
import { toJobSkills, yearsToFill, type SuggestedSkills } from '@/lib/skillPriority'

export type JobInfoOpeningsProp =
  | {
      mode: 'create'
      value: OpeningRow[]
      onChange: (rows: OpeningRow[]) => void
      onValidityChange?: (valid: boolean) => void
    }
  | { mode: 'edit'; jobId: string; onManage: () => void }

interface JobInfoStepProps {
  jobData: Partial<CreateJobData>
  onUpdate: (data: Partial<CreateJobData>) => void
  openings?: JobInfoOpeningsProp
  /** The saved job, once it exists: its source documents feed Gio. */
  jobId?: string | null
  /** Only these cards (the CRM setup splits the form over two steps); all by default. */
  sections?: JobInfoSection[]
  /** A job from a Gio Sales deal: its client is the deal's, shown read-only. */
  clientLocked?: string | null
  /** The job opens from elsewhere (the CRM setup's Review step), not from a status here. */
  hideStatus?: boolean
}

export type JobInfoSection = 'basics' | 'location' | 'compensation' | 'description' | 'skills'

/** A Gio function's error as one specific line (never the raw server text). */
async function gioErrorMessage(err: any, fallback: string): Promise<string> {
  const ctx = err?.context
  const status = ctx?.status as number | undefined
  if (status === 429) return 'Too many requests. Try again in a moment.'
  if (status === 402) return 'AI credits exhausted. Add credits in Settings → Workspace → Usage.'
  try {
    const parsed = ctx ? await ctx.json?.() : null
    if (parsed?.message && parsed?.error !== 'internal_error') return parsed.message
  } catch { /* keep the fallback */ }
  return fallback
}

function OpeningsReadOnlyField({ jobId, onManage }: { jobId: string; onManage: () => void }) {
  const { openings } = useJobOpenings(jobId)
  return (
    <div>
      <FieldLabel>Openings</FieldLabel>
      <div
        className="mt-2 flex items-center gap-2 font-inter"
        style={{ height: 36, background: '#FAFAF7', border: '1px solid #EDEBE5', borderRadius: 8, padding: '0 12px', fontSize: 12.5, color: '#1F2230' }}
      >
        <Hash className="h-3.5 w-3.5 text-[#8B8F9E]" />
        <span className="truncate">{setupSummaryText(openings)}</span>
        <button
          type="button"
          onClick={onManage}
          className="ml-auto underline underline-offset-2"
          style={{ color: '#1F2230', textDecorationColor: '#B9B6AC' }}
        >
          Manage
        </button>
      </div>
      <FieldHint>Req IDs and target dates are managed in Job setup → Openings.</FieldHint>
    </div>
  )
}

type JobStatus = 'draft' | 'open' | 'closed' | 'archived'

const STATUS_OPTIONS = [
  { value: 'draft' as JobStatus, label: 'Draft' },
  { value: 'open' as JobStatus, label: 'Open' },
  { value: 'closed' as JobStatus, label: 'Closed' },
  { value: 'archived' as JobStatus, label: 'Archived' },
]

const WORK_MODE_OPTIONS: { value: JobWorkMode; label: string; icon: React.ComponentType<any> }[] = [
  { value: 'remote', label: 'Remote', icon: Globe },
  { value: 'hybrid', label: 'Hybrid', icon: Building2 },
  { value: 'onsite', label: 'On-site', icon: MapPin },
]

const EMPLOYMENT_TYPE_OPTIONS: { value: JobEmploymentType; label: string }[] = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'contract', label: 'Contract' },
  { value: 'internship', label: 'Internship' },
  { value: 'temporary', label: 'Temporary' },
]

const JOB_LEVEL_OPTIONS: SearchableSelectOption[] = [
  { value: 'L1 — Specialists', label: 'L1 — Specialists' },
  { value: 'L2 — Senior Specialists', label: 'L2 — Senior Specialists' },
  { value: 'L3 — Staff', label: 'L3 — Staff' },
  { value: 'L4 — Principal', label: 'L4 — Principal' },
  { value: 'L5 — Manager', label: 'L5 — Manager' },
  { value: 'L6 — Director', label: 'L6 — Director' },
  { value: 'L7 — VP', label: 'L7 — VP' },
  { value: 'L8 — Executive', label: 'L8 — Executive' },
]

export function JobInfoStep({ jobData, onUpdate, openings, jobId, sections, clientLocked, hideStatus }: JobInfoStepProps) {
  const show = (k: JobInfoSection) => !sections || sections.includes(k)
  const [isDeptFormOpen, setIsDeptFormOpen] = React.useState(false)
  const { data: childOrgs = [], isLoading: isLoadingOrgs } = useChildOrganizationsForJobCreation(jobData.organization_id)
  const { userType, organizationId } = useAuth()
  const permissions = usePermissions()
  const { departments, isLoading: isLoadingDepts, getDefault, createDepartment } = useDepartments()

  const organizationOptions: SearchableSelectOption[] = React.useMemo(
    () =>
      childOrgs
        .map((org) => ({ value: org.id, label: org.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [childOrgs]
  )

  const departmentOptions: SearchableSelectOption[] = React.useMemo(
    () =>
      departments
        .map((d) => ({ value: d.id, label: d.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [departments]
  )

  // TA Ops pick the client: platform admins, workspace owners and admins (Allan, 2026-10-20).
  const canSelectOrganization = permissions.isPlatformAdmin || permissions.isWorkspaceOwner || permissions.isAdmin

  React.useEffect(() => {
    if (!jobData.organization_id && userType !== 'platform_admin' && organizationId) {
      onUpdate({ organization_id: organizationId })
    }
  }, [jobData.organization_id, userType, organizationId, onUpdate])

  // Default department to "General" on first load if not set
  React.useEffect(() => {
    if (!jobData.department_id && !isLoadingDepts) {
      const def = getDefault()
      if (def) onUpdate({ department_id: def.id })
    }
  }, [jobData.department_id, isLoadingDepts, getDefault, onUpdate])

  // Defaults
  React.useEffect(() => {
    const patch: Partial<CreateJobData> = {}
    if (jobData.show_salary_public === undefined) patch.show_salary_public = true
    if (jobData.status === undefined) patch.status = 'open'
    if (jobData.currency === undefined) patch.currency = 'USD'
    if (Object.keys(patch).length) onUpdate(patch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const set = <K extends keyof CreateJobData>(field: K, value: CreateJobData[K]) =>
    onUpdate({ [field]: value } as Partial<CreateJobData>)

  const handleCreateDepartment = async (data: { name: string; description?: string | null }) => {
    try {
      const result = await createDepartment.mutateAsync(data)
      setIsDeptFormOpen(false)
      if (result?.id) onUpdate({ department_id: result.id })
    } catch (error) {
      console.error('Failed to create department:', error)
    }
  }

  const salaryInvalid =
    jobData.salary_min != null &&
    jobData.salary_max != null &&
    jobData.salary_min > jobData.salary_max

  const [isGenerating, setIsGenerating] = React.useState(false)
  const [genValidationMsg, setGenValidationMsg] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!genValidationMsg) return
    const t = setTimeout(() => setGenValidationMsg(null), 4000)
    return () => clearTimeout(t)
  }, [genValidationMsg])

  const handleGenerateDescription = async () => {
    const title = (jobData.title || '').trim()
    if (!title) {
      setGenValidationMsg('Add at least a job title so Gio can draft a description.')
      return
    }
    setGenValidationMsg(null)
    if (
      (jobData.description || '').trim() &&
      !(await confirmDialog({
        title: 'Replace the current description?',
        description: 'Gio drafts a new one from the job details, your text and the source documents.',
        confirmLabel: 'Replace description',
      }))
    )
      return
    setIsGenerating(true)
    try {
      const { data, error } = await supabase.functions.invoke('generate-job-description', {
        body: { jobData, jobId: jobId ?? null },
      })
      if (error) throw error
      if (!data?.description) throw new Error('Empty response')
      set('description', data.description as string)
      const used = (data.sources_used as string[] | undefined)?.length ?? 0
      toast.success(used ? `Description drafted from ${used} document${used === 1 ? '' : 's'}` : 'Description drafted')
    } catch (err: any) {
      toast.error(await gioErrorMessage(err, "Gio couldn't draft the description. Try again."))
    } finally {
      setIsGenerating(false)
    }
  }

  // Skills by tier from Gio: a preview first, applied only on "Use these skills".
  const [isSuggesting, setIsSuggesting] = React.useState(false)
  const [suggested, setSuggested] = React.useState<SuggestedSkills | null>(null)
  const handleSuggestSkills = async () => {
    if (!(jobData.title || '').trim()) {
      toast.error('Add a job title so Gio can suggest skills.')
      return
    }
    setIsSuggesting(true)
    try {
      const { data, error } = await supabase.functions.invoke('generate-job-skills', {
        body: { jobData, jobId: jobId ?? null },
      })
      if (error) throw error
      if (!data?.skills) throw new Error('Empty response')
      setSuggested({ ...(data.skills as SuggestedSkills), experience: data.experience ?? null, leftOut: data.left_out ?? [] })
    } catch (err: any) {
      toast.error(await gioErrorMessage(err, "Gio couldn't suggest skills. Try again."))
    } finally {
      setIsSuggesting(false)
    }
  }
  const applySuggested = () => {
    if (!suggested) return
    // Years fill only empty fields; a range the recruiter typed stays.
    const years = yearsToFill(suggested, jobData)
    onUpdate({ ...toJobSkills(suggested), ...years })
    setSuggested(null)
    toast.success('Skills updated')
  }

  return (
    <div className="space-y-8">
      {/* ------------------------------------------------ BASICS */}
      {show('basics') && (
      <SectionCard title="Basics">
        <div>
          <FieldLabel htmlFor="title" required>
            Job title
          </FieldLabel>
          <Input
            id="title"
            value={jobData.title || ''}
            onChange={(e) => set('title', e.target.value)}
            placeholder="e.g. Senior Product Designer"
            className="mt-2"
          />
          <FieldHint>The public-facing title candidates will see.</FieldHint>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <FieldLabel htmlFor="internal_title" optional>
              Internal title
            </FieldLabel>
            <Input
              id="internal_title"
              value={jobData.internal_title || ''}
              onChange={(e) => set('internal_title', e.target.value)}
              placeholder="e.g. Sr. PD, Design Systems"
              className="mt-2"
            />
            <FieldHint>Used in CRM &amp; analytics only.</FieldHint>
          </div>

          {!hideStatus && (
          <div>
            <FieldLabel required>Status</FieldLabel>
            <div className="mt-2">
              <Segmented
                ariaLabel="Job status"
                options={STATUS_OPTIONS}
                value={(jobData.status as JobStatus) || 'open'}
                onChange={(v) => set('status', v)}
              />
            </div>
          </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {clientLocked != null ? (
            <div>
              <FieldLabel>Client</FieldLabel>
              <div
                className="mt-2 flex items-center gap-2 font-inter"
                style={{ height: 36, background: '#FAFAF7', border: '1px solid #EDEBE5', borderRadius: 8, padding: '0 12px', fontSize: 12.5, color: '#1F2230' }}
              >
                <Building2 className="h-3.5 w-3.5 text-[#8B8F9E]" />
                <span className="truncate">{clientLocked || 'The deal\u2019s client'}</span>
              </div>
              <FieldHint>From the deal in Gio Sales.</FieldHint>
            </div>
          ) : canSelectOrganization && (
            <div>
              <FieldLabel required>Client</FieldLabel>
              <div className="mt-2">
                <SearchableSelect
                  options={organizationOptions}
                  value={jobData.organization_id || ''}
                  onValueChange={(value) => set('organization_id', value)}
                  placeholder={isLoadingOrgs ? 'Loading…' : 'Select a client…'}
                  searchPlaceholder="Search clients…"
                  emptyMessage="No clients found."
                  disabled={isLoadingOrgs}
                />
              </div>
              <ClientsManagedNote />
            </div>
          )}

          <div>
            <FieldLabel required>Department</FieldLabel>
            <div className="mt-2">
              <SearchableSelect
                options={departmentOptions}
                value={jobData.department_id || ''}
                onValueChange={(value) => set('department_id', value)}
                placeholder={isLoadingDepts ? 'Loading…' : 'Select a department…'}
                searchPlaceholder="Search departments…"
                emptyMessage="No departments found."
                disabled={isLoadingDepts}
                onCreateNew={() => setIsDeptFormOpen(true)}
                createNewLabel="Create department"
              />
            </div>
            <FieldHint>Function area (Sales, People, Engineering…). Shared across clients.</FieldHint>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <FieldLabel>Job level</FieldLabel>
            <div className="mt-2">
              <SearchableSelect
                options={JOB_LEVEL_OPTIONS}
                value={jobData.job_level || ''}
                onValueChange={(value) => set('job_level', value)}
                placeholder="Select level…"
                searchPlaceholder="Search levels…"
                emptyMessage="No levels."
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <FieldLabel required>Priority</FieldLabel>
            <div className="mt-2">
              <JobPriorityPicker
                value={jobData.priority ?? DEFAULT_JOB_PRIORITY}
                onChange={(v) => set('priority', v)}
              />
            </div>
            <FieldHint>
              Ranks this req against the rest — drives default sort and escalation on the Job Dashboard.
            </FieldHint>
          </div>
        </div>

        {openings && (
          <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid #F1F0EC' }}>
            {openings.mode === 'create' ? (
              <>
                <FieldLabel required>Openings</FieldLabel>
                <FieldHint>
                  One row per hire. Req IDs come from your HRIS or headcount plan — Gio suggests the next number.
                  Each opening's target hire date drives pacing on the Job Dashboard.
                </FieldHint>
                <div className="mt-3">
                  <OpeningsEditor
                    mode="create"
                    value={openings.value}
                    onChange={openings.onChange}
                    onValidityChange={openings.onValidityChange}
                  />
                </div>
              </>
            ) : (
              <OpeningsReadOnlyField jobId={openings.jobId} onManage={openings.onManage} />
            )}
          </div>
        )}

      </SectionCard>
      )}

      {/* ----------------------------------- LOCATION & EMPLOYMENT */}
      {show('location') && (
      <SectionCard title="Location & Employment">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <FieldLabel required>Work mode</FieldLabel>
            <Select
              value={jobData.work_mode || ''}
              onValueChange={(v) => set('work_mode', v as JobWorkMode)}
            >
              <SelectTrigger className="mt-2 h-11">
                <SelectValue placeholder="Select work mode…" />
              </SelectTrigger>
              <SelectContent>
                {WORK_MODE_OPTIONS.map((opt) => {
                  const Icon = opt.icon
                  return (
                    <SelectItem key={opt.value} value={opt.value}>
                      <span className="inline-flex items-center gap-2">
                        <Icon className="h-3.5 w-3.5 text-text-tertiary" />
                        {opt.label}
                      </span>
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
          </div>

          <div>
            <FieldLabel required>Employment type</FieldLabel>
            <Select
              value={jobData.employment_type || ''}
              onValueChange={(v) => set('employment_type', v as JobEmploymentType)}
            >
              <SelectTrigger className="mt-2 h-11">
                <SelectValue placeholder="Select employment type…" />
              </SelectTrigger>
              <SelectContent>
                {EMPLOYMENT_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <span className="inline-flex items-center gap-2">
                      <Briefcase className="h-3.5 w-3.5 text-text-tertiary" />
                      {opt.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <FieldLabel htmlFor="location">Primary location</FieldLabel>
            <div className="relative mt-2">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary pointer-events-none" />
              <Input
                id="location"
                value={jobData.location || ''}
                onChange={(e) => set('location', e.target.value)}
                placeholder="e.g. New York, NY"
                className="pl-9"
              />
            </div>
            <FieldHint>City, state — or 'Remote'.</FieldHint>
          </div>

          <div>
            <FieldLabel optional>Additional locations</FieldLabel>
            <div className="mt-2">
              <ChipInput
                values={jobData.additional_locations || []}
                onChange={(next) => set('additional_locations', next)}
                placeholder="Add location…"
                tone="purple"
              />
            </div>
            <FieldHint>Cities where the role may sit on-site.</FieldHint>
          </div>
        </div>
      </SectionCard>
      )}

      {/* ----------------------------------------- COMPENSATION (job-level) */}
      {show('compensation') && (
      <SectionCard title="Compensation">
        <p className="-mt-1 mb-4 text-[12.5px] text-text-tertiary">
          Stored on the job. Visibility, equity, signing bonus, and commission live on the job post (Step 4).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <div>
            <FieldLabel>Currency</FieldLabel>
            <div className="mt-2">
              <CurrencySelect
                value={jobData.currency || 'USD'}
                onChange={(v) => set('currency', v)}
              />
            </div>
          </div>
          <div>
            <FieldLabel>Min salary</FieldLabel>
            <div className="mt-2">
              <SalaryInput
                value={jobData.salary_min ?? undefined}
                onChange={(v) => set('salary_min', v)}
                placeholder="80,000"
                invalid={salaryInvalid}
              />
            </div>
            {salaryInvalid && <FieldHint tone="error">Min must be lower than max</FieldHint>}
          </div>
          <div>
            <FieldLabel>Max salary</FieldLabel>
            <div className="mt-2">
              <SalaryInput
                value={jobData.salary_max ?? undefined}
                onChange={(v) => set('salary_max', v)}
                placeholder="120,000"
              />
            </div>
          </div>
        </div>
        <div className="mt-5">
          <FieldLabel>Pay period</FieldLabel>
          <div className="mt-2">
            <Seg
              label="Pay period"
              value={(jobData.salary_period ?? '') as 'monthly' | 'annual'}
              onChange={(v: 'monthly' | 'annual') => set('salary_period', v)}
              options={[
                { value: 'monthly', label: 'Per month' },
                { value: 'annual', label: 'Per year' },
              ]}
            />
          </div>
          <FieldHint>What the range above is per. The salary screening rule needs it.</FieldHint>
        </div>
      </SectionCard>
      )}







      {/* ----------------------------------------- JOB DESCRIPTION */}
      {show('description') && (
      <SectionCard
        title="Job description"
        trailing={
          <Button
            variant="ghost"
            size="xs"
            icon={Sparkles}
            className="text-virgilio-purple"
            onClick={handleGenerateDescription}
            loading={isGenerating}
            disabled={isGenerating}
          >
            Draft with Gio
          </Button>
        }
      >
        <JobFilesField jobId={jobId} />
        <div>
          <FieldLabel htmlFor="description" required>
            Description
          </FieldLabel>
          <Textarea
            id="description"
            value={jobData.description || ''}
            onChange={(e) => set('description', e.target.value)}
            placeholder="## About the client
…

## About the job
…

## What you'll do
- …

## What we're looking for
- …

## Why join the client
- …"
            className="mt-2 min-h-[220px] font-inter text-[13px] leading-relaxed"
          />
          {genValidationMsg ? (
            <p className="mt-2 text-[12px] text-destructive font-inter">{genValidationMsg}</p>
          ) : (
            <FieldHint>Markdown. Draft with Gio writes About the client, About the job, What you'll do, What we're looking for and Why join; the offer stays out of it.</FieldHint>
          )}
        </div>
      </SectionCard>
      )}

      {/* ------------------------------------------ REQUIRED SKILLS */}
      {show('skills') && (
      <SectionCard
        title="Required skills"
        trailing={
          <Button
            variant="ghost"
            size="xs"
            icon={Sparkles}
            className="text-virgilio-purple"
            onClick={handleSuggestSkills}
            loading={isSuggesting}
            disabled={isSuggesting}
          >
            Generate with Gio
          </Button>
        }
      >
        {suggested && (
          <SkillSuggestions
            value={suggested}
            onChange={setSuggested}
            onApply={applySuggested}
            onCancel={() => setSuggested(null)}
            replacing={(jobData.skills || []).length > 0}
            years={yearsToFill(suggested, jobData)}
          />
        )}
        <div>
          <FieldLabel required>Skills</FieldLabel>
          <div className="mt-2">
            <ChipInput
              values={jobData.skills || []}
              onChange={(next) => set('skills', next)}
              placeholder="Add skill…"
              tone="purple"
              priorities={jobData.skill_priorities || {}}
              onPriorityChange={(skill, next) => set('skill_priorities', { ...(jobData.skill_priorities || {}), [skill]: next })}
            />
          </div>
          <FieldHint>Press Enter to add. Open a skill's chevron to mark it as a must-have, nice-to-have or extra; Gio Fit scores against that.</FieldHint>
          <SkillPriorityLegend className="mt-2" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <FieldLabel>Min years of experience</FieldLabel>
            <div className="relative mt-2">
              <TrendingUp className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary pointer-events-none" />
              <Input
                type="number"
                min={0}
                value={jobData.min_years_experience ?? ''}
                onChange={(e) =>
                  set('min_years_experience', e.target.value ? Number(e.target.value) : undefined)
                }
                placeholder="0"
                className="pl-9 pr-14"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-text-tertiary pointer-events-none">
                years
              </span>
            </div>
          </div>
          <div>
            <FieldLabel optional>Max years</FieldLabel>
            <div className="relative mt-2">
              <Input
                type="number"
                min={0}
                value={jobData.max_years_experience ?? ''}
                onChange={(e) =>
                  set('max_years_experience', e.target.value ? Number(e.target.value) : undefined)
                }
                placeholder="—"
                className="pr-14"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-text-tertiary pointer-events-none">
                years
              </span>
            </div>
          </div>
        </div>
      </SectionCard>
      )}

      {/* Department Creation Dialog */}
      <DepartmentFormDialog
        open={isDeptFormOpen}
        onOpenChange={setIsDeptFormOpen}
        onSubmit={handleCreateDepartment}
        isSubmitting={createDepartment.isPending}
      />
    </div>
  )
}

// Convenience re-export so JobWizard can render the badge in the header
export { AiAssistedBadge }
